/** Reading a live scene document: its nodes, the cues bound to them, and the assets they name. */

import type { SceneDocument } from "@nebulize/sdk";

/* The barrel exports the document and not its parts, so the parts are derived from it here. */
export type SceneNode = NonNullable<SceneDocument["nodes"]>[number];
export type CueBinding = NonNullable<SceneDocument["cue_bindings"]>[number];
export type AssetReference = NonNullable<SceneNode["asset"]>;

/** One document and who is drawing it, which is how every row below says where it came from. */
export interface NamedScene {
  /** The customer's name for the added activity, never the package id. */
  from: string;
  document: SceneDocument;
}

/** Every node of a document, parents before children. */
export function nodesOf(document: SceneDocument): SceneNode[] {
  const out: SceneNode[] = [];
  const walk = (nodes: readonly SceneNode[]): void => {
    for (const node of nodes) {
      out.push(node);
      walk(node.children ?? []);
    }
  };
  walk(document.nodes ?? []);
  return out;
}

/** One cue binding, with the canvas kinds the node it paints is visible on. */
export interface CueRow {
  id: string;
  from: string;
  tagPattern: string;
  nodeId: string;
  presentation: string;
  /** The kinds the bound node names. Empty reaches nobody, and the row says so. */
  canvases: string[];
  /** Whether the document holds a node with that id at all. */
  bound: boolean;
}

/** Every cue binding the live scenes declare, in document order. */
export function cueRows(scenes: readonly NamedScene[]): CueRow[] {
  const out: CueRow[] = [];
  for (const { from, document } of scenes) {
    const nodes = nodesOf(document);
    for (const binding of document.cue_bindings ?? []) {
      const node = nodes.find((one) => one.id === binding.node_id);
      out.push({
        id: `${document.activity_id}:${document.canvas_kind}:${binding.tag_pattern}:${binding.node_id}`,
        from,
        tagPattern: binding.tag_pattern,
        nodeId: binding.node_id,
        presentation: binding.presentation,
        canvases: node === undefined ? [] : [...node.canvases],
        bound: node !== undefined,
      });
    }
  }
  return out;
}

/** One asset the scenes name, and every place that names it. */
export interface AssetRow {
  contentHash: string;
  mime: string;
  sizeBytes: number | null;
  provider: string;
  /** Each reference as `<added activity> on <canvas kind>`. */
  references: string[];
}

function reference(scene: NamedScene): string {
  return `${scene.from} on ${scene.document.canvas_kind}`;
}

function note(held: Map<string, AssetRow>, asset: AssetReference, where: string): void {
  const found = held.get(asset.content_hash);
  if (found === undefined) {
    held.set(asset.content_hash, {
      contentHash: asset.content_hash,
      mime: asset.mime,
      sizeBytes: asset.size_bytes ?? null,
      provider: asset.provider ?? "",
      references: [where],
    });
    return;
  }
  if (!found.references.includes(where)) found.references.push(where);
}

/** Every asset the live scenes name, content-addressed, deduplicated across documents. */
export function assetRows(scenes: readonly NamedScene[]): AssetRow[] {
  const held = new Map<string, AssetRow>();
  for (const scene of scenes) {
    const where = reference(scene);
    const background = scene.document.background;
    if (background != null && "asset" in background) note(held, background.asset, where);
    for (const node of nodesOf(scene.document)) {
      if (node.asset != null) note(held, node.asset, where);
    }
  }
  return [...held.values()];
}
